/**
 * Auth Service
 * API calls for authentication and member binding
 */

import { get, post, patch } from './api';
import type { UserProfile } from 'blia-events-shared';

// Response types
export interface BindingStatusResponse {
  status: 'unbound' | 'pending' | 'bound' | 'inactive';
  memberNumber: string | null;
  memberName: string | null;
  associations: Array<{
    associationId: string;
    associationName: string;
    isAdmin: boolean;
  }>;
  pendingMemberNumber?: string;
}

export interface BindingVerifyRequest {
  memberNumber: string;
  chineseName?: string;
  englishFirstName?: string;
  englishLastName?: string;
}

export interface BindingVerifyResponse {
  verified: boolean;
  memberPreview?: {
    memberNumber: string;
    nameZh: string;
    nameEn: string | null;
    associationName: string;
    maskedPhone: string | null;
    maskedEmail: string | null;
  };
  extendedDetails?: ExtendedMemberDetails;
  errorCode?: string;
  errorMessage?: string;
  // For admin-registered records that will be merged
  hasExistingRegistrations?: boolean;
  existingRegistrationCount?: number;
  // BINDING_CONTACT_ALREADY_BOUND: the login that has the record, masked
  boundLogin?: { maskedEmail: string; provider: string | null };
  // Proof of the name check; /binding/confirm refuses without it (expires in 10 min)
  verificationToken?: string;
}

// i18n organization name structure (Chinese and English)
export interface OrganizationName {
  nameZh: string | null;
  nameEn: string | null;
}

// Extended organization name with ID fields for geographic data
export interface OrganizationNameWithId extends OrganizationName {
  id?: string;
  dataverseId?: string;
}

export interface ExtendedMemberDetails {
  contactId: string;
  memberNumber: string;
  // Binding type - 'venerable' for venerables without BLIA membership, 'member' for regular members
  bindingType: 'member' | 'venerable';
  // Associated temple for venerables (輔導法師)
  associatedTemple: string | null;
  venerableTitle: string | null;  // Role/title (e.g., Abbot, Supervisor)
  // Display-only fields
  gender: { code: number | null; labelEn: string | null; labelZh: string | null };
  chapterOrganization: OrganizationName | null;   // Chapter (協會) from _blia_chapter_value - fresh from Dataverse
  parentOrganization: OrganizationName | null;    // Subchapter (分會) from _parentcustomerid_value
  yadOrganization: OrganizationName | null;       // YAD chapter name (if linked)
  scoutsOrganization: OrganizationName | null;    // Scouts chapter name (if linked)
  groupMemberOrganization: OrganizationName | null;  // Group member org (團體會員)
  // Geographic info from chapter (with IDs for venerables)
  continent: OrganizationNameWithId | null;       // From chapter's continent lookup
  country: OrganizationNameWithId | null;         // From chapter's country lookup
  elderAdvisorChapter: OrganizationNameWithId | null; // From subchapter's elder advisor lookup
  // Editable fields
  otherName: string | null;             // Other name (e.g., passport name) from blia_othername
  passportNumber: string | null;
  passportExpiryDate: string | null;
  birthDate: string | null;
  mobilePhone: string | null;
  telephone1: string | null;
  telephone2: string | null;
  email: string | null;
  isVenerable: boolean | null;          // Is Venerable (法師)
  eventNationality: string | null;      // Event nationality
  languages: number[];                  // Multi-select language values
  tshirtSize: number | null;            // T-shirt size code
  healthRecord: string | null;          // Health record free text
  roommatePreferenceName: string | null;  // Preferred roommate name
  roommatePreferenceReason: string | null; // Reason for roommate preference
  // Flags for mandatory field detection
  mandatoryFields: {
    passportRequired: boolean;
    passportExpiryRequired: boolean;
    birthdateRequired: boolean;
  };
}

// Gender options matching Dataverse global choice
export const GENDER_OPTIONS: Array<{ value: number; labelEn: string; labelZh: string }> = [
  { value: 1, labelEn: 'Male', labelZh: '男' },
  { value: 2, labelEn: 'Female', labelZh: '女' },
];

// Language options matching Dataverse global choice
export const LANGUAGE_OPTIONS: Array<{ value: number; labelEn: string; labelZh: string }> = [
  { value: 285980000, labelEn: 'Chinese', labelZh: '中文' },
  { value: 285980001, labelEn: 'English', labelZh: '英語' },
  { value: 285980002, labelEn: 'Cantonese', labelZh: '粵語' },
  { value: 285980003, labelEn: 'French', labelZh: '法語' },
  { value: 285980004, labelEn: 'German', labelZh: '德語' },
  { value: 285980005, labelEn: 'Japanese', labelZh: '日語' },
  { value: 285980006, labelEn: 'Spanish', labelZh: '西班牙語' },
  { value: 575100000, labelEn: 'Vietnamese', labelZh: '越南語' },
  { value: 575100001, labelEn: 'Hokkien', labelZh: '閩南語' },
  { value: 575100002, labelEn: 'Hakka', labelZh: '客家語' },
  { value: 575100003, labelEn: 'Indigenous', labelZh: '原住民語' },
  { value: 575100004, labelEn: 'Korean', labelZh: '韓語' },
  { value: 575100005, labelEn: 'Portuguese', labelZh: '葡萄牙語' },
  { value: 575100006, labelEn: 'Arabic', labelZh: '阿拉伯語' },
  { value: 575100007, labelEn: 'Hindi', labelZh: '印地語' },
  { value: 575100008, labelEn: 'Indonesian', labelZh: '印尼語' },
  { value: 575100009, labelEn: 'Italian', labelZh: '意大利語' },
];

// T-shirt size options matching Dataverse blia_eventtshirtsize
export const TSHIRT_SIZE_OPTIONS: Array<{ value: number; labelEn: string; labelZh: string }> = [
  { value: 285980000, labelEn: 'XS', labelZh: 'XS' },
  { value: 285980001, labelEn: 'S', labelZh: 'S' },
  { value: 285980002, labelEn: 'M', labelZh: 'M' },
  { value: 285980003, labelEn: 'L', labelZh: 'L' },
  { value: 285980004, labelEn: 'XL', labelZh: 'XL' },
  { value: 285980005, labelEn: '2XL', labelZh: '2XL' },
  { value: 285980006, labelEn: '3XL', labelZh: '3XL' },
  { value: 285980007, labelEn: '4XL', labelZh: '4XL' },
  { value: 285980008, labelEn: '5XL', labelZh: '5XL' },
  { value: 285980009, labelEn: '6XL', labelZh: '6XL' },
  { value: 285980010, labelEn: 'Other', labelZh: '其他' },
];

export interface MemberDetailsUpdateRequest {
  otherName?: string;         // Other name (e.g., passport name)
  passportNumber?: string;
  passportExpiryDate?: string;
  birthDate?: string;
  mobilePhone?: string;
  telephone1?: string;
  telephone2?: string;
  email?: string;
  isVenerable?: boolean;
  eventNationality?: string;
  languages?: number[];
  healthRecord?: string;
  associatedTemple?: string; // For venerables - can update their temple
  venerableTitle?: string;   // For venerables - role/title (e.g., Abbot, Supervisor)
  roommatePreferenceName?: string;
  roommatePreferenceReason?: string;
  // Geographic fields for venerables
  continentId?: string;
  countryId?: string;
  chapterDataverseId?: string;
}

// Organization type mappings (from Dataverse global choice: OrganizationType)
export const ORG_TYPE_LABELS: Record<number, { en: string; zh: string }> = {
  285980000: { en: 'Headquarters', zh: '總會' },
  285980001: { en: 'Chapter', zh: '協會' },
  285980002: { en: 'Subchapter', zh: '分會' },
  285980003: { en: 'YAD', zh: '青年團' },
  285980004: { en: 'Scouts', zh: '童軍團' },
  285980005: { en: 'Advisory Committee', zh: '督導委員會' },
  285980006: { en: 'Temple', zh: '道場' },
  285980007: { en: 'Group Member', zh: '團體會員' },
};

// Member role from Dataverse blia_memberrole table
export interface MemberRole {
  id: string;
  roleName: string;
  roleNameZh: string;
  roleNameEn: string | null;
  startDate: string | null;
  endDate: string | null;
  isActive: boolean;
  term: {
    id: string;
    name: string;
    nameEn: string | null;
  } | null;
  organization: {
    id: string;
    name: string;
    nameEn: string | null;
    type: {
      code: number;
      labelZh: string;
      labelEn: string;
    };
    // For headquarters special case: 'world' for 世界總會, 'roc' for 中華總會
    headquartersType?: 'world' | 'roc' | null;
  } | null;
  note: string | null;
}

export interface BindingConfirmRequest {
  memberNumber: string;
  verificationToken?: string;
  memberDetails?: MemberDetailsUpdateRequest;
}

export interface VenerableBindingRequest {
  nameZh: string;
  nameEn?: string;
  associatedTemple: string;
  passportNumber: string;
  passportExpiryDate: string;
  birthDate: string;
  genderCode?: number;
  email?: string;
  mobilePhone?: string;
  telephone1?: string;
  telephone2?: string;
  eventNationality?: string;
  languages?: number[];
  healthRecord?: string;
  venerableTitle?: string;    // Role/title (e.g., Abbot, Supervisor)
  // Geographic fields for conference number generation
  continentId: string;        // Required - continent for conference number prefix
  countryId?: string;         // Optional - country
  chapterDataverseId?: string; // Optional - chapter/org
}

export interface BindingConfirmResponse {
  success: boolean;
  userProfile: UserProfile;
  errorCode: string | null;
  errorMessage: string | null;
}

export interface LogoutResponse {
  message: string;
}

/**
 * Get current user profile
 */
export async function getMe(): Promise<UserProfile> {
  const response = await get<{ data: UserProfile }>('/auth/me');
  return response.data;
}

/**
 * Logout current user
 */
export async function logout(): Promise<void> {
  await post<LogoutResponse>('/auth/logout');
}

/**
 * Get current binding status
 */
export async function getBindingStatus(): Promise<BindingStatusResponse> {
  const response = await get<{ data: BindingStatusResponse }>('/binding/status');
  return response.data;
}

/**
 * Verify member binding credentials
 */
export async function verifyBinding(
  data: BindingVerifyRequest
): Promise<BindingVerifyResponse> {
  const response = await post<{ data: BindingVerifyResponse }>('/binding/verify', data);
  return response.data;
}

/**
 * Confirm and complete member binding
 */
export async function confirmBinding(
  data: BindingConfirmRequest
): Promise<BindingConfirmResponse> {
  const response = await post<{ data: BindingConfirmResponse }>('/binding/confirm', data);
  return response.data;
}

/**
 * Confirm binding for venerable (輔導法師) without BLIA membership
 */
export async function confirmVenerableBinding(
  data: VenerableBindingRequest
): Promise<BindingConfirmResponse> {
  const response = await post<{ data: BindingConfirmResponse }>('/binding/venerable-confirm', data);
  return response.data;
}

/**
 * Get extended member details for additional info step
 */
export async function getMemberDetails(): Promise<ExtendedMemberDetails> {
  const response = await get<{ data: ExtendedMemberDetails }>('/binding/member-details');
  return response.data;
}

/**
 * Update member details in Dataverse
 */
export async function updateMemberDetails(
  data: MemberDetailsUpdateRequest
): Promise<void> {
  await patch<{ success: boolean }>('/binding/member-details', data);
}

/**
 * Get member roles from Dataverse
 * Returns all roles for the current user with organization and term details
 */
export async function getMemberRoles(): Promise<MemberRole[]> {
  const response = await get<{ data: MemberRole[] }>('/binding/member-roles');
  return response.data;
}

/**
 * Sync organizations response type
 */
export interface SyncOrganizationsResponse {
  associations: Array<{
    associationId: string;
    nameZh: string | null;
    nameEn: string | null;
    isPrimary: boolean;
  }>;
  syncedAt: string;
}

/**
 * Sync organization associations from Dataverse
 * Refreshes the user's organization data (for regular members)
 */
export async function syncOrganizations(): Promise<SyncOrganizationsResponse> {
  const response = await post<{ data: SyncOrganizationsResponse }>('/binding/sync-organizations', {});
  return response.data;
}

/**
 * Redirect to Azure AD login
 */
export function redirectToLogin(returnUrl?: string): void {
  const redirectUri = returnUrl || window.location.pathname;
  window.location.href = `/.auth/login/aad?post_login_redirect_uri=${encodeURIComponent(redirectUri)}`;
}

/**
 * Redirect to Google login
 */
export function redirectToGoogleLogin(returnUrl?: string): void {
  const redirectUri = returnUrl || window.location.pathname;
  window.location.href = `/.auth/login/google?post_login_redirect_uri=${encodeURIComponent(redirectUri)}`;
}

/**
 * Redirect to Azure AD logout
 */
export function redirectToLogout(returnUrl?: string): void {
  const redirectUri = returnUrl || '/';
  window.location.href = `/.auth/logout?post_logout_redirect_uri=${encodeURIComponent(redirectUri)}`;
}

/**
 * Get EasyAuth user info from /.auth/me
 */
export async function getEasyAuthInfo(): Promise<{
  clientPrincipal: {
    userId: string;
    userRoles: string[];
    identityProvider: string;
    userDetails: string;
    claims: Array<{ typ: string; val: string }>;
  } | null;
}> {
  try {
    const response = await fetch('/.auth/me');
    if (!response.ok) {
      return { clientPrincipal: null };
    }
    const data = await response.json();
    return data;
  } catch {
    return { clientPrincipal: null };
  }
}

export const authService = {
  getMe,
  logout,
  getBindingStatus,
  verifyBinding,
  confirmBinding,
  confirmVenerableBinding,
  getMemberDetails,
  updateMemberDetails,
  getMemberRoles,
  syncOrganizations,
  redirectToLogin,
  redirectToGoogleLogin,
  redirectToLogout,
  getEasyAuthInfo,
};

export default authService;
